import { useCallback, useEffect, useMemo, useState } from "react";
import {
  lerFiltros,
  lerLiberacoes,
  lerTravados,
  liberarTentativas,
  type AlunoTravado,
  type FiltrosDoAdminPos,
  type LiberacaoRegistrada,
} from "../lib/adminPos";
import { CampoDeBusca } from "./ui";
import Carregando from "./Carregando";

// Admin › Faculdade › Tentativas — a terceira tela que a secretaria pediu, e
// Victor mandou fazer em 14/09/2026: **liberar tentativas**.
//
// ---------------------------------------------------------------------------
// A lista é de quem está TRAVADO, não de quem esgotou
// ---------------------------------------------------------------------------
// Medido em 14/09: **2.804** pares (aluno, disciplina) gastaram as cinco
// tentativas, e só **29** estão sem aprovação. Os outros 2.775 usaram tudo e
// passaram — não precisam de nada.
//
// Uma lista com 99% de gente que já passou é uma lista que ninguém usa: quem
// atende teria de conferir aluno por aluno se o caso é real. Por isso o banco
// cruza as duas condições (`pos_admin_travados`, 000224), e a tela mostra 29.
//
// ---------------------------------------------------------------------------
// A TURMA só aparece depois do CURSO
// ---------------------------------------------------------------------------
// Regra de Victor, em 14/09: *"jamais mostrar a lista de turmas sem antes
// selecionar o curso"*. E ela tem número: as 59 turmas usam só **20 números**,
// com "Turma 1" existindo em **9 cursos**. O rótulo do filtro é
// `Turma <n> · <m> matriculados`, sem o curso — então sem curso escolhido a
// lista ofereceria nove opções com o mesmo nome, distinguíveis só pela
// contagem de matriculados, que é informação sobre o TAMANHO e não sobre qual é
// qual.
//
// O campo não fica desabilitado em silêncio: ele diz o que falta.
//
// ---------------------------------------------------------------------------
// O MOTIVO é obrigatório
// ---------------------------------------------------------------------------
// É a decisão de `sec_reprovado_explica`: liberação sem motivo é um número que
// ninguém sabe explicar seis meses depois — e é a primeira coisa que se pergunta
// quando o próximo aluno pedir a mesma coisa. Quem recusa é o banco; a tela só
// não deixa o botão parecer disponível antes.

export default function AdminPosTentativas() {
  const [filtros, setFiltros] = useState<FiltrosDoAdminPos | null>(null);
  const [curso, setCurso] = useState<string>("");
  const [turma, setTurma] = useState<string>("");
  const [busca, setBusca] = useState("");
  const [travados, setTravados] = useState<AlunoTravado[] | null>(null);
  const [liberacoes, setLiberacoes] = useState<LiberacaoRegistrada[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);

  useEffect(() => {
    void lerFiltros().then(setFiltros);
  }, []);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    const [t, l] = await Promise.all([
      lerTravados(curso || null, busca || undefined, turma || null),
      lerLiberacoes(null),
    ]);
    setTravados(t);
    setLiberacoes(l);
    setErro(t === null);
    setCarregando(false);
  }, [curso, turma, busca]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  // Só as turmas DAQUELE curso — e só quando há curso. O `curso ?` é a regra,
  // não uma otimização: sem ele a lista cairia nas 59 de oito cursos.
  const turmasDoCurso = useMemo(
    () => (curso ? (filtros?.turmas ?? []).filter((t) => t.curso_slug === curso) : []),
    [filtros, curso],
  );

  // Trocar de curso limpa a turma: uma turma da CGT com o curso da Perícia
  // devolveria zero linhas, e o operador leria isso como "ninguém travado".
  function trocarCurso(novo: string) {
    setCurso(novo);
    setTurma("");
  }

  if (carregando && travados === null) return <Carregando />;

  if (erro) {
    return (
      <div className="rounded-cf-lg border border-erro-borda bg-erro-fundo p-4 text-sm text-erro-tinta">
        Não conseguimos carregar a lista. Tente recarregar a página.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <header>
        <h2 className="font-display text-lg font-bold text-titulo">Tentativas</h2>
        <p className="mt-1 text-sm text-cinza">
          Alunos que gastaram as cinco tentativas de uma disciplina e ainda não atingiram a nota
          mínima. Quem esgotou e passou não aparece aqui.
        </p>
      </header>

      <div className="surface flex flex-wrap items-end gap-4 rounded-cf-lg p-4">
        <div>
          <label className="block text-xs font-bold tracking-widest text-cinza uppercase" htmlFor="curso-travados">
            Curso
          </label>
          <select
            id="curso-travados"
            value={curso}
            onChange={(e) => trocarCurso(e.target.value)}
            className="mt-1.5 h-11 rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
          >
            <option value="">todos</option>
            {(filtros?.cursos ?? []).map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.nome ?? c.slug}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold tracking-widest text-cinza uppercase" htmlFor="turma-travados">
            Turma
          </label>
          <select
            id="turma-travados"
            value={turma}
            onChange={(e) => setTurma(e.target.value)}
            disabled={!curso}
            aria-describedby={curso ? undefined : "turma-travados-ajuda"}
            className="mt-1.5 h-11 rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
          >
            <option value="">{curso ? "todas as turmas" : "escolha o curso"}</option>
            {turmasDoCurso.map((t) => (
              <option key={t.id} value={t.id}>
                Turma {t.numero} · {t.matriculados} matriculado{t.matriculados === 1 ? "" : "s"}
              </option>
            ))}
          </select>
          {/* O campo desabilitado DIZ o que falta. Desabilitar em silêncio faz o
              operador achar que a ferramenta quebrou — é a lição do "Limite com
              a prorrogação" do HUB DP: o ramo em que a peça não serve precisa de
              texto próprio. */}
          {!curso && (
            <p id="turma-travados-ajuda" className="mt-1 text-xs text-cinza">
              Escolha o curso primeiro.
            </p>
          )}
        </div>
        <div className="min-w-60 flex-1">
          <label className="block text-xs font-bold tracking-widest text-cinza uppercase" htmlFor="busca-travados">
            Aluno
          </label>
          {/* A busca é por aluno porque o caso real é o aluno LIGANDO — a mesma
              descoberta da fila de documentos e do painel de notas. */}
          <CampoDeBusca
            id="busca-travados"
            valor={busca}
            aoMudar={setBusca}
            rotulo="Buscar aluno por nome ou e-mail"
            placeholder="nome ou e-mail"
          />
        </div>
      </div>

      <section>
        <h3 className="text-xs font-bold tracking-widest text-cinza uppercase">
          Travados ({(travados ?? []).length})
        </h3>
        <ul className="mt-2 flex flex-col gap-2">
          {(travados ?? []).map((t) => (
            <LinhaDoTravado
              key={`${t.user_id}|${t.disciplina_id}`}
              travado={t}
              aberto={aberto === `${t.user_id}|${t.disciplina_id}`}
              aoAbrir={() =>
                setAberto(
                  aberto === `${t.user_id}|${t.disciplina_id}` ? null : `${t.user_id}|${t.disciplina_id}`,
                )
              }
              aoLiberar={recarregar}
            />
          ))}
          {(travados ?? []).length === 0 && (
            // Zero aqui é o DESFECHO desejado, não uma lista vazia por falta de
            // dado — e a frase diz isso, em vez de deixar o operador achar que o
            // filtro quebrou.
            <li className="rounded-cf-lg bg-aviso-fundo p-3 text-sm text-aviso-tinta">
              Ninguém está travado {curso || turma || busca ? "neste recorte" : "agora"}.
            </li>
          )}
        </ul>
      </section>

      {(liberacoes ?? []).length > 0 && (
        <section>
          <h3 className="text-xs font-bold tracking-widest text-cinza uppercase">
            Liberações já dadas ({(liberacoes ?? []).length})
          </h3>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-150 text-sm">
              <thead>
                <tr className="border-b border-borda text-left text-xs text-cinza uppercase">
                  <th className="p-2 font-bold">Aluno</th>
                  <th className="p-2 font-bold">Disciplina</th>
                  <th className="p-2 font-bold">Extras</th>
                  <th className="p-2 font-bold">Motivo</th>
                  <th className="p-2 font-bold">Quem</th>
                  <th className="p-2 font-bold">Quando</th>
                </tr>
              </thead>
              <tbody>
                {(liberacoes ?? []).map((l) => (
                  <tr key={l.id} className="border-b border-borda/60">
                    <td className="p-2 text-tinta">{l.aluno_nome ?? l.aluno_email}</td>
                    <td className="p-2 text-cinza">{l.disciplina}</td>
                    <td className="p-2 text-tinta tabular-nums">+{l.tentativas_extras}</td>
                    <td className="p-2 text-cinza">{l.motivo}</td>
                    <td className="p-2 text-cinza">{l.liberado_por ?? "—"}</td>
                    <td className="p-2 text-cinza whitespace-nowrap tabular-nums">
                      {new Date(l.criado_em).toLocaleDateString("pt-BR")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function LinhaDoTravado({
  travado,
  aberto,
  aoAbrir,
  aoLiberar,
}: {
  travado: AlunoTravado;
  aberto: boolean;
  aoAbrir: () => void;
  aoLiberar: () => Promise<void>;
}) {
  const [extras, setExtras] = useState(1);
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<{ tom: "ok" | "erro"; texto: string } | null>(null);

  async function liberar() {
    setSalvando(true);
    const r = await liberarTentativas(travado.user_id, travado.disciplina_id, extras, motivo);
    setSalvando(false);
    if (!r.ok) {
      // Erro de UMA liberação fica NA LINHA — trocar a lista inteira por uma
      // mensagem apagaria da tela os outros travados, que estão certos. É a
      // cicatriz do `alternarFeito` do Calendário.
      setRecado({ tom: "erro", texto: r.detalhe ?? "Não deu para liberar." });
      return;
    }
    // O que o operador precisa saber é o ESTADO NOVO: o aluno já pode fazer a
    // prova? O número vem do banco, não de uma soma feita aqui.
    setRecado({
      tom: "ok",
      texto: `Liberado. O aluno tem ${r.restantes} ${r.restantes === 1 ? "tentativa" : "tentativas"} agora.`,
    });
    await aoLiberar();
  }

  return (
    <li className="surface rounded-cf-lg" data-travado={travado.disciplina_id}>
      <div className="flex flex-wrap items-center gap-3 p-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-tinta">
            {travado.aluno_nome ?? travado.aluno_email}
          </p>
          <p className="truncate text-xs text-cinza">
            {travado.disciplina} · {travado.tentativas} tentativas · melhor nota{" "}
            {travado.melhor_nota === null
              ? "—"
              : travado.melhor_nota.toFixed(1).replace(".", ",")}
          </p>
        </div>
        <button
          type="button"
          onClick={aoAbrir}
          aria-expanded={aberto}
          className="min-h-10 rounded-full border border-borda px-4 text-sm font-semibold text-tinta transition hover:border-laranja"
        >
          {aberto ? "Fechar" : "Liberar tentativas"}
        </button>
      </div>

      {aberto && (
        <div className="flex flex-wrap items-end gap-3 border-t border-borda p-3">
          <div>
            <label
              className="block text-xs font-bold tracking-widest text-cinza uppercase"
              htmlFor={`extras-${travado.user_id}-${travado.disciplina_id}`}
            >
              Quantas
            </label>
            <input
              id={`extras-${travado.user_id}-${travado.disciplina_id}`}
              type="number"
              min={1}
              max={10}
              value={extras}
              onChange={(e) => setExtras(Number(e.target.value))}
              className="mt-1.5 h-11 w-24 rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
            />
          </div>
          <div className="min-w-60 flex-1">
            <label
              className="block text-xs font-bold tracking-widest text-cinza uppercase"
              htmlFor={`motivo-${travado.user_id}-${travado.disciplina_id}`}
            >
              Por quê
            </label>
            <input
              id={`motivo-${travado.user_id}-${travado.disciplina_id}`}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="ex.: prova caiu no meio, atestado médico"
              className="mt-1.5 h-11 w-full rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={liberar}
            disabled={!motivo.trim() || salvando}
            className={
              motivo.trim() && !salvando ? "pill-cta min-h-11 px-5" : "pill-desligada min-h-11 px-5"
            }
          >
            {salvando ? "Liberando…" : "Liberar"}
          </button>

          {recado && (
            <p
              role="status"
              className={`w-full rounded-cf p-2.5 text-sm ${
                recado.tom === "ok" ? "bg-aviso-fundo text-aviso-tinta" : "bg-erro-fundo text-erro-tinta"
              }`}
            >
              {recado.texto}
            </p>
          )}
        </div>
      )}
    </li>
  );
}
